/**
 * Call Provider / Context
 * ---
 * Configures the general state of a Daily call, such as which features
 * to enable, as well as instantiate the 'call machine' hook responsible
 * for the overaching call loop (joining, leaving, etc)
 */
import React from 'react';
import DailyIframe from '@daily-co/daily-js';
import { DailyProvider } from '@daily-co/daily-react-hooks';
// import { useRouter } from 'next/router';
import {
  ACCESS_STATE_LOBBY,
  ACCESS_STATE_UNKNOWN,
  VIDEO_QUALITY_AUTO,
} from '../constants';
import { useNetworkState } from '../hooks/useNetworkState';
import { useCallMachine } from './useCallMachine';
import { CallAppEffects } from '../../hocs/events';
import { ConferenceRest } from '../../../../../common/rest/conference/conferenceRest';
import { useSharedState } from '../hooks/useSharedState';
import { SyncStorage } from '../../../../../common/utils/local-storage/local-storage-helpers';

export const CallContext = React.createContext();

const BACKGROUND_BLUR_KEY = 'CALL_BACKGROUND_BLUR';
const blurStorage = new SyncStorage();
// try/catch: localStorage itself can throw (private mode, blocked site data)
const readBackgroundBlur = () => {
  try { return !!blurStorage.getItem(BACKGROUND_BLUR_KEY, false); } catch (e) { return false; }
};
const writeBackgroundBlur = (on) => {
  try { blurStorage.saveItem(BACKGROUND_BLUR_KEY, on); } catch (e) { /* storage unavailable */ }
};
const BLUR_PROCESSOR = { type: 'background-blur', config: { strength: 1 } };
const NO_PROCESSOR = { type: 'none' };

export const CallProvider = ({
  children,
  domain,
  room,
  token = '',
  // Initial value from the create/join REST response, so a participant who never touched Settings
  // still starts agreeing with the room's real value. Arrives async, so applied via an effect below.
  initialSaveLevel,
  subscribeToTracksAutomatically = true,
  cleanURLOnJoin = false,
}) => {
  // const router = useRouter();
  const [roomInfo, setRoomInfo] = React.useState(null);
  const [enableScreenShare, setEnableScreenShare] = React.useState(false);
  const [enableJoinSound, setEnableJoinSound] = React.useState(true);
  const [videoQuality, setVideoQuality] = React.useState(VIDEO_QUALITY_AUTO);
  const [showLocalVideo, setShowLocalVideo] = React.useState(true);
  const [preJoinNonAuthorized, setPreJoinNonAuthorized] = React.useState(false);
  const [enableRecording, setEnableRecording] = React.useState(null);
  const [startCloudRecording, setStartCloudRecording] = React.useState(false);
  const [roomExp, setRoomExp] = React.useState(null);

  // Daily CallMachine hook (primarily handles status of the call)
  const { daily, ensureCallObject, leave, state, setRedirectOnLeave } = useCallMachine({
    domain,
    room,
    token,
    subscribeToTracksAutomatically,
  });

  // saveLevel is call-wide, not per-tab — shared across participants via Daily app-message broadcast,
  // so everyone (not just whoever opened Settings) agrees on it. callObjectOverride is required since
  // useCallState() only resolves an ANCESTOR's context, not this component's own about-to-be-provided value.
  const { sharedState: saveLevelState, setSharedState: setSaveLevelShared } = useSharedState({
    initialValues: { saveLevel: 'video' },
    callObjectOverride: daily,
    channel: 'saveLevel',
  });
  const saveLevel = saveLevelState.saveLevel;
  // Pre-join, `room` is still empty (no conference row exists yet) — a choice made there can't be
  // persisted until the room exists, so remember it was touched and flush it once `room` shows up.
  const saveLevelPendingRef = React.useRef(false);
  // A local choice always wins over the bootstrap value below — never overwrite the user's own
  // pre-join pick with, say, a freshly-created conference's 'video' default arriving moments later.
  const saveLevelTouchedRef = React.useRef(false);
  // Bootstrap below must run at most once (a standing sync would re-broadcast a stale initialSaveLevel
  // over every later change from other participants, reverting them).
  const saveLevelBootstrappedRef = React.useRef(false);
  const setSaveLevel = React.useCallback((next) => {
    saveLevelTouchedRef.current = true;
    setSaveLevelShared({ saveLevel: next });
    saveLevelPendingRef.current = true;
  }, [setSaveLevelShared]);
  // LeaveModal needs to know the persist below actually reached the DB before triggering /process,
  // or a level switched right before "Finish" can lose that race.
  const saveLevelSyncPromiseRef = React.useRef(Promise.resolve());
  React.useEffect(() => {
    if (!room || !saveLevelPendingRef.current) return;
    saveLevelPendingRef.current = false;
    saveLevelSyncPromiseRef.current = ConferenceRest.setSettings(room, { saveLevel })
      .catch((e) => console.error('failed to persist saveLevel', e));
  }, [room, saveLevel]);
  const ensureSaveLevelSynced = React.useCallback(() => saveLevelSyncPromiseRef.current, []);
  // Bootstrap from the persisted value once the create/join REST call resolves (it lands async,
  // after this component has already mounted with initialSaveLevel undefined) — but only if the
  // user hasn't already made — or started making — their own choice in the meantime.
  React.useEffect(() => {
    if (!initialSaveLevel || saveLevelTouchedRef.current || saveLevelBootstrappedRef.current) return;
    saveLevelBootstrappedRef.current = true;
    if (initialSaveLevel === saveLevel) return;
    setSaveLevelShared({ saveLevel: initialSaveLevel });
  }, [initialSaveLevel, setSaveLevelShared]);

  const networkState = useNetworkState(daily, videoQuality);

  // Own-camera background blur (desktop browsers only — Daily's processor needs WebGL2, no mobile).
  // Remembered in this browser, so the next call starts with it.
  const canBlurBackground = React.useMemo(() => !!DailyIframe.supportedBrowser().supportsVideoProcessing, []);
  const [backgroundBlur, setBackgroundBlurState] = React.useState(() => canBlurBackground && readBackgroundBlur());
  const setBackgroundBlur = React.useCallback((on) => {
    setBackgroundBlurState(on);
    writeBackgroundBlur(on);
  }, []);
  // The first enable loads Daily's segmentation model and takes a few seconds — drives a loader.
  const [backgroundBlurPending, setBackgroundBlurPending] = React.useState(false);
  // A new call object starts without a processor; skip the no-op "none" for one that never had blur.
  const blurAppliedRef = React.useRef({ daily: null, on: false });
  // Bumped when Daily's real processor state is found to differ from ours (see the join check below).
  const [blurRecheck, setBlurRecheck] = React.useState(0);
  React.useEffect(() => {
    if (!daily || !canBlurBackground) return;
    const applied = blurAppliedRef.current;
    const prevOn = applied.daily === daily ? applied.on : false;
    if (prevOn === backgroundBlur) return;
    blurAppliedRef.current = { daily, on: backgroundBlur };
    let current = true;
    setBackgroundBlurPending(true);
    daily.updateInputSettings({
      video: { processor: backgroundBlur ? BLUR_PROCESSOR : NO_PROCESSOR },
    })
      .catch((e) => {
        console.error('background blur', e);
        // Not applied — show the previous state (the stored preference stays, it's a transient failure).
        if (!current) return;
        blurAppliedRef.current = { daily, on: prevOn };
        setBackgroundBlurState(prevOn);
      })
      .finally(() => { if (current) setBackgroundBlurPending(false); });
    // Also ends a loader whose request is abandoned, e.g. the call object is destroyed mid-flight.
    return () => { current = false; setBackgroundBlurPending(false); };
  }, [daily, backgroundBlur, canBlurBackground, blurRecheck]);
  // Daily drops a processor that fails after starting (and turns the camera off) — reflect that in the switch.
  React.useEffect(() => {
    if (!daily || !canBlurBackground) return;
    const onError = (ev) => {
      // Daily's types put `type` on the event, some docs under `error` — accept both.
      if ((ev?.type ?? ev?.error?.type) !== 'video-processor-error') return;
      console.error('background blur failed', ev);
      blurAppliedRef.current = { daily, on: false };
      setBackgroundBlurState(false);
    };
    daily.on('nonfatal-error', onError);
    return () => { daily.off('nonfatal-error', onError); };
  }, [daily, canBlurBackground]);
  // Blur is applied pre-join (for the preview); if join() dropped it, Daily's own state says so — re-apply.
  React.useEffect(() => {
    if (!daily || !canBlurBackground) return;
    const onJoined = async () => {
      let actualOn;
      try {
        actualOn = (await daily.getInputSettings())?.video?.processor?.type === 'background-blur';
      } catch (e) { return; }
      if (actualOn === blurAppliedRef.current.on) return;
      blurAppliedRef.current = { daily, on: actualOn };
      setBlurRecheck((n) => n + 1);
    };
    daily.on('joined-meeting', onJoined);
    return () => { daily.off('joined-meeting', onJoined); };
  }, [daily, canBlurBackground]);

  // Feature detection taken from daily room object and client browser support
  React.useEffect(() => {
    if (!daily) return;
    const updateRoomConfigState = async () => {
      const roomConfig = await daily.room();
      if (!roomConfig) return;
      const isOob = !!roomConfig.config?.owner_only_broadcast;
      const owner = roomConfig.tokenConfig?.is_owner;
      const config = roomConfig?.config;

      setRoomInfo(roomConfig);
      if (config?.lang) {
        CallAppEffects.setLang(config.lang);
      }

      const fullUI = !isOob || (isOob && owner);

      if (!config) return;

      if (config.exp) {
        setRoomExp(config.exp * 1000 || Date.now() + 1 * 60 * 1000);
      }
      const recordingType =
        roomConfig?.tokenConfig?.enable_recording ??
        roomConfig?.config?.enable_recording;

      // Always cloud-recording, so gate only on a configured recording type.
      // The old browser check left this null in Firefox, so recording handlers
      // never attached and stopRecording() was a no-op.
      if (recordingType) {
        setEnableRecording(recordingType);
        setStartCloudRecording(
          roomConfig?.tokenConfig?.start_cloud_recording ?? false
        );
      }
      setEnableScreenShare(
        fullUI &&
        (roomConfig?.tokenConfig?.enable_screenshare ??
          roomConfig?.config?.enable_screenshare) &&
        DailyIframe.supportedBrowser().supportsScreenShare
      );
    };
    updateRoomConfigState();
  }, [state, daily]);

  // Convience wrapper for adding a fake participant to the call
  const addFakeParticipant = React.useCallback(() => {
    daily.addFakeParticipant();
  }, [daily]);

  // Convenience wrapper for changing the bandwidth of the client
  const setBandwidth = React.useCallback(
    (quality) => {
      daily.setBandwidth(quality);
    },
    [daily]
  );

  React.useEffect(() => {
    if (!daily) return;

    const { access } = daily.accessState();
    if (access === ACCESS_STATE_UNKNOWN) return;

    const requiresPermission = access?.level === ACCESS_STATE_LOBBY;
    setPreJoinNonAuthorized(requiresPermission && !token);
  }, [state, daily, token]);

  React.useEffect(() => {
    if (!daily) return;

    if (cleanURLOnJoin) {
      // daily.on('joined-meeting', () => router.replace(`/${room}`));
      daily.on('joined-meeting', () => console.log(`/${room}`));
    }

    // return () => daily.off('joined-meeting', () => router.replace(`/${room}`));
    return () => {
      daily.off('joined-meeting', () => router.replace(`/${room}`));
    }
  }, [cleanURLOnJoin, daily, room]);

  return (
    <CallContext.Provider
      value={{
        state,
        // Known synchronously (it's the route param the room was created/joined with), unlike
        // roomInfo below which waits on an async daily.room() fetch — use this wherever a room
        // identifier is needed right away (e.g. persisting a setting from the pre-join popup).
        room,
        callObject: daily,
        ensureCallObject,
        addFakeParticipant,
        preJoinNonAuthorized,
        leave,
        networkState,
        showLocalVideo,
        roomExp,
        enableRecording,
        enableScreenShare,
        enableJoinSound,
        videoQuality,
        setVideoQuality,
        roomInfo,
        setRoomInfo,
        saveLevel,
        setSaveLevel,
        ensureSaveLevelSynced,
        canBlurBackground,
        backgroundBlur,
        backgroundBlurPending,
        setBackgroundBlur,
        setBandwidth,
        setRedirectOnLeave,
        setShowLocalVideo,
        setEnableScreenShare,
        startCloudRecording,
        subscribeToTracksAutomatically,
        setEnableJoinSound
      }}
    >
      <DailyProvider callObject={daily}>{children}</DailyProvider>
    </CallContext.Provider>
  );
};

// CallProvider.propTypes = {
//   children: PropTypes.node,
//   domain: PropTypes.string.isRequired,
//   room: PropTypes.string.isRequired,
//   token: PropTypes.string,
//   subscribeToTracksAutomatically: PropTypes.bool,
// };

export const useCallState = () => React.useContext(CallContext);
